/**
 * React wrapper for Bryntum GridBase
 */
import React, { RefObject } from 'react';
import { AjaxStore, AjaxStoreConfig, AlignSpec, Base, CellCopyPaste, CellCopyPasteConfig, CellMenu, CellMenuConfig, CellTooltip, CellTooltipConfig, CheckColumnConfig, Column, ColumnAutoWidth, ColumnAutoWidthConfig, ColumnDragToolbar, ColumnDragToolbarConfig, ColumnPicker, ColumnPickerConfig, ColumnRename, ColumnRenameConfig, ColumnReorder, ColumnReorderConfig, ColumnResize, ColumnResizeConfig, ColumnStore, ColumnStoreConfig, Container, ContainerItemConfig, ContainerLayoutConfig, DragHelper, Editor, ExportDialog, Field, FileDrop, FileDropConfig, FillHandle, FillHandleConfig, Filter, FilterBar, FilterBarConfig, FilterConfig, Grid, GridBase, GridBaseFeaturesConfigType, GridCellEdit, GridCellEditConfig, GridColumnConfig, GridExcelExporter, GridExcelExporterConfig, GridGroupSummary, GridGroupSummaryConfig, GridPdfExport, GridPdfExportConfig, GridPrint, GridPrintConfig, GridRowReorder, GridRowReorderConfig, GridRowResize, GridRowResizeConfig, GridSplit, GridSplitConfig, GridSummary, GridSummaryConfig, Group, GroupConfig, HeaderMenu, HeaderMenuConfig, Layout, Location, Mask, MaskConfig, Menu, MenuItem, MenuItemConfig, MergeCells, MergeCellsConfig, Model, ModelConfig, Panel, PanelCollapserConfig, PanelCollapserOverlayConfig, PanelHeader, Popup, PreserveScrollOptions, QuickFind, QuickFindConfig, RecordPositionContext, Rectangle, RegionResize, RegionResizeConfig, Row, RowCopyPaste, RowCopyPasteConfig, RowExpander, RowExpanderConfig, RowNumberColumnConfig, ScrollManager, ScrollManagerConfig, Scroller, ScrollerConfig, Search, SearchConfig, Sort, SortConfig, StateProvider, StickyCells, StickyCellsConfig, Store, StoreConfig, StoreSearchResult, Stripe, StripeConfig, SubGrid, SubGridConfig, TabConfig, Tool, ToolConfig, ToolbarConfig, Tooltip, TooltipConfig, Tree, TreeConfig, TreeGroup, TreeGroupConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumGridBaseProps =  {
     // Configs only
    adopt?                         : HTMLElement|string
    align?                         : AlignSpec|string
    allowOver?                     : boolean
    anchor?                        : boolean
    anchorToTarget?                : boolean
    animateRemovingRows?           : boolean
    ariaDescription?               : string
    ariaLabel?                     : string
    autoClose?                     : boolean
    autoHeight?                    : boolean
    autoHide?                      : boolean
    autoShow?                      : boolean
    autoUpdateRecord?              : boolean
    bbar?                          : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                       : string|object
    bubbleEvents?                  : object
    callOnFunctions?               : boolean
    centered?                      : boolean
    closable?                      : boolean
    closeAction?                   : 'hide'|'destroy'
    collapsed?                     : boolean
    collapsible?                   : boolean|Partial<PanelCollapserConfig>|Partial<PanelCollapserOverlayConfig>
    columns?                       : ColumnStore|Partial<GridColumnConfig>[]|Partial<ColumnStoreConfig>
    config?                        : object
    constrainTo?                   : HTMLElement|Widget|Rectangle
    contentElementCls?             : string|object
    contextMenuTriggerEvent?       : 'contextmenu'|'click'|'dblclick'
    defaultBindProperty?           : string
    defaultFocus?                  : (widget: Widget) => boolean
    defaultRegion?                 : string
    defaults?                      : object
    destroyStore?                  : boolean
    disableGridRowModelWarning?    : boolean
    dismissDelay?                  : number
    dock?                          : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                     : boolean|{
        handleSelector?: string
    }
    enableSticky?                  : boolean
    enableTextSelection?           : boolean
    features?                      : Partial<GridBaseFeaturesConfigType>
    fillLastColumn?                : boolean
    fixedRowHeight?                : boolean
    floating?                      : boolean
    focusOnToFront?                : boolean
    footer?                        : {
        dock?: 'top'|'right'|'bottom'|'left'|'start'|'end'
        html?: string
        cls?: string
    }|string
    forElement?                    : HTMLElement
    forSelector?                   : string
    fullRowRefresh?                : boolean
    getHtml?                       : ((context: { tip: Tooltip, element: HTMLElement, activeTarget: HTMLElement, event: Event }) => string|Promise<any>)|string
    getRowHeight?                  : (getRowHeight: { record: Model }) => number
    header?                        : string|boolean|PanelHeader
    hideAnimation?                 : boolean|object
    hideDelay?                     : number|boolean
    hideHorizontalScrollbar?       : boolean
    hideOnDelegateChange?          : boolean
    hideWhenEmpty?                 : boolean
    hoverDelay?                    : number
    htmlCls?                       : string|object
    ignoreParentReadOnly?          : boolean
    itemCls?                       : string
    keyMap?                        : Record<string, string>
    lazyItems?                     : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?                     : object
    loadingMsg?                    : string
    loadMask?                      : string|Partial<MaskConfig>|null
    loadMaskDefaults?              : Partial<MaskConfig>
    loadMaskError?                 : Partial<MaskConfig>|Mask|boolean
    localeClass?                   : typeof Base
    localizable?                   : boolean
    localizableProperties?         : string[]
    maskDefaults?                  : Partial<MaskConfig>
    masked?                        : boolean|string|Partial<MaskConfig>
    maximizable?                   : boolean
    maximized?                     : boolean
    modal?                         : boolean|{
        closeOnMaskTap?: boolean
        transparent?: boolean
    }
    monitorResize?                 : boolean
    mouseOffsetX?                  : number
    mouseOffsetY?                  : number
    namedItems?                    : Record<string, Partial<ContainerItemConfig>>
    owner?                         : Widget
    plugins?                       : Function[]
    positioned?                    : boolean
    preserveFocusOnDatasetChange?  : boolean
    preserveScrollOnDatasetChange? : boolean
    preventTooltipOnTouch?         : boolean
    relayStoreEvents?              : boolean
    resizeToFitIncludesHeader?     : boolean
    responsiveLevels?              : Record<string, number|string>
    ripple?                        : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?                   : ShadowRoot
    scrollAction?                  : 'hide'|'realign'|null
    scrollerClass?                 : typeof Scroller
    scrollManager?                 : Partial<ScrollManagerConfig>|ScrollManager
    selectionMode?                 : object
    showAnimation?                 : boolean|object
    showDirty?                     : boolean|{
        duringEdit?: boolean
    }
    showOnClick?                   : boolean
    showOnHover?                   : boolean
    showTooltipWhenDisabled?       : boolean
    stateful?                      : boolean|object|string[]
    statefulEvents?                : object|string[]
    stateId?                       : string
    stateProvider?                 : StateProvider
    strips?                        : Record<string, Partial<ContainerItemConfig>>
    subGridConfigs?                : Record<string, Partial<SubGridConfig>>
    syncMask?                      : string|Partial<MaskConfig>|null
    tab?                           : boolean|Partial<TabConfig>
    tag?                           : string
    tbar?                          : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    textAlign?                     : 'left'|'center'|'right'|'start'|'end'
    textContent?                   : boolean
    trackMouse?                    : boolean
    trapFocus?                     : boolean
    ui?                            : string|object
    weight?                        : number

     // Configs and properties
    alignSelf?             : string
    animateTreeNodeToggle? : boolean
    appendTo?              : HTMLElement|string
    cls?                   : string|object
    columnLines?           : boolean
    content?               : string
    data?                  : object[]|Model[]|Partial<ModelConfig>[]
    dataset?               : object
    disabled?              : boolean
    emptyText?             : string
    enableUndoRedoKeys?    : boolean
    extraData?             : string|number|boolean|object
    flex?                  : number|string
    height?                : number|string
    hidden?                : boolean
    hideFooters?           : boolean
    hideHeaders?           : boolean
    html?                  : string|((widget: Widget) => string)
    id?                    : string
    insertBefore?          : HTMLElement|string
    insertFirst?           : HTMLElement|string
    items?                 : (Partial<ContainerItemConfig>|Partial<MenuItemConfig>|Widget)[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>>|Widget[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>|boolean|null>
    layout?                : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?           : object
    longPressTime?         : number
    margin?                : number|string
    maxHeight?             : string|number
    maximizeOnMobile?      : number|string
    maxWidth?              : string|number
    minHeight?             : string|number
    minWidth?              : string|number
    preserveScroll?        : PreserveScrollOptions|boolean
    readOnly?              : boolean
    record?                : Model
    rowHeight?             : number
    rowLines?              : boolean
    scrollable?            : Scroller|boolean|Partial<ScrollerConfig>
    store?                 : Partial<Store|StoreConfig|AjaxStore|AjaxStoreConfig>
    strictRecordMapping?   : boolean
    title?                 : string
    tools?                 : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?               : string|Partial<TooltipConfig>
    transitionDuration?    : number
    width?                 : number|string
    x?                     : number
    y?                     : number

     // Properties only
    anchorSize?      : number[]
    isSettingValues? : boolean
    isValid?         : boolean
    selectedCell?    : Location
    selectedCells?   : Location[]
    selectedRecord?  : Model
    selectedRecords? : Model[]|number[]
    selectedRows?    : Model[]|number[]
    state?           : object
    type?            : string
    values?          : Record<string, object>

     // Events
    onBeforeCancelCellEdit?      : ((event: { source: Grid, editorContext: Location }) => boolean|void)|string
    onBeforeCellDelete?          : ((event: { source: Grid, gridSelection: (Location|Model)[] }) => boolean|void)|string
    onBeforeCellEditStart?       : ((event: { source: Grid, editorContext: { column: Column, record: Model, cell: HTMLElement, editor: Field, finalize: { context?: object }, value: any } }) => boolean|void)|string
    onBeforeClose?               : ((event: { source: Popup }) => void)|string
    onBeforeColumnDragStart?     : ((event: { source: Grid, column: Column, event: Event }) => boolean|void)|string
    onBeforeColumnDropFinalize?  : ((event: { source: Grid, column: Column, insertBefore: Column, newParent: Column, event: Event, region: string }) => boolean|void)|string
    onBeforeCopy?                : ((event: { source: Grid, cells: Location[], data: string, isCut: boolean, entityName: string }) => Promise<boolean|void>)|string
    onBeforeDestroy?             : ((event: { source: Base }) => void)|string
    onBeforeFinishCellEdit?      : ((event: { grid: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onBeforeHide?                : ((event: { source: Widget }) => boolean|void)|string
    onBeforePaste?               : ((event: { source: Grid, clipboardData: string, targetCell: Location, entityName: string }) => Promise<boolean|void>)|string
    onBeforePdfExport?           : ((event: { config: object }) => boolean|void)|string
    onBeforeRenderRow?           : ((event: { source: Grid, row: Row, record: Model, recordIndex: number }) => void)|string
    onBeforeRenderRows?          : ((event: { source: Grid }) => void)|string
    onBeforeSelectionChange?     : ((event: { action: string, mode: 'row'|'cell', source: Grid, deselected: Model[], selected: Model[], selection: Model[], deselectedCells: Location[], selectedCells: Location[], cellSelection: Location[] }) => boolean|void)|string
    onBeforeSetRecord?           : ((event: { source: Container, record: Model }) => void)|string
    onBeforeShow?                : ((event: { source: { triggeredByEvent: Event } }) => Promise<boolean|void>)|string
    onBeforeToggleGroup?         : ((event: { groupRecord: Model, collapse: boolean, domEvent: Event }) => boolean|void)|string
    onBeforeToggleNode?          : ((event: { source: Grid, record: Model, collapse: boolean }) => void)|string
    onCancelCellEdit?            : ((event: { source: Grid, editorContext: Location, event: Event }) => void)|string
    onCatchAll?                  : ((event: {[key: string]: any, type: string}) => void)|string
    onCellClick?                 : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellContextMenu?           : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellDblClick?              : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellMenuBeforeShow?        : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, record: Model }) => boolean|void)|string
    onCellMenuItem?              : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, record: Model }) => void)|string
    onCellMenuShow?              : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, record: Model }) => void)|string
    onCellMenuToggleItem?        : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, record: Model, checked: boolean }) => void)|string
    onCellMouseOut?              : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellMouseOver?             : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCollapse?                  : ((event: { source: Panel }) => void)|string
    onCollapseNode?              : ((event: { source: Grid, record: Model }) => void)|string
    onColumnDrag?                : ((event: { source: Grid, column: Column, insertBefore: Column, event: Event, context: { valid: boolean } }) => void)|string
    onColumnDragStart?           : ((event: { source: Grid, column: Column, event: Event }) => void)|string
    onColumnDrop?                : ((event: { source: Grid, column: Column, insertBefore: Column, newParent: Column, valid: boolean, event: Event, region: string }) => boolean|void)|string
    onContextMenuItem?           : ((event: { source: Widget, menu: Menu, item: MenuItem }) => void)|string
    onContextMenuToggleItem?     : ((event: { source: Widget, menu: Menu, item: MenuItem, checked: boolean }) => void)|string
    onCopy?                      : ((event: { source: Grid, cells: Location[], copiedDataString: string, isCut: boolean, entityName: string }) => void)|string
    onDataChange?                : ((event: { source: Grid, store: Store, action: 'remove'|'removeAll'|'add'|'updatemultiple'|'clearchanges'|'filter'|'update'|'dataset'|'replace', record: Model, records: Model[], changes: object }) => void)|string
    onDestroy?                   : ((event: { source: Base }) => void)|string
    onDragSelecting?             : ((event: { source: Grid, selectedCells: Model[]|object|Partial<ModelConfig> }) => void)|string
    onExpand?                    : ((event: { source: Panel }) => void)|string
    onExpandNode?                : ((event: { source: Grid, record: Model }) => void)|string
    onFileDrop?                  : ((event: { source: Grid, file: DataTransferItem, domEvent: DragEvent }) => void)|string
    onFinishCellEdit?            : ((event: { grid: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onFocusIn?                   : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?                  : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onGridRowAbort?              : ((event: { source: DragHelper, context: object, event: MouseEvent }) => void)|string
    onGridRowBeforeDragStart?    : ((event: { source: DragHelper, context: { records: Model[] }, event: MouseEvent|TouchEvent }) => boolean|void)|string
    onGridRowBeforeDropFinalize? : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, records: Model[], oldPositionContext: RecordPositionContext[] }, event: MouseEvent }) => Promise<boolean|void>)|string
    onGridRowDrag?               : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, records: Model[] }, event: MouseEvent }) => void)|string
    onGridRowDragStart?          : ((event: { source: DragHelper, context: { records: Model[] }, event: MouseEvent|TouchEvent }) => void)|string
    onGridRowDrop?               : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, record: Model, records: Model[], oldPositionContext: RecordPositionContext[] }, event: MouseEvent }) => void)|string
    onHeaderMenuBeforeShow?      : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => boolean|void)|string
    onHeaderMenuItem?            : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column }) => void)|string
    onHeaderMenuShow?            : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => void)|string
    onHeaderMenuToggleItem?      : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, checked: boolean }) => void)|string
    onHide?                      : ((event: { source: Widget }) => void)|string
    onMouseOut?                  : ((event: { event: MouseEvent }) => void)|string
    onMouseOver?                 : ((event: { event: MouseEvent }) => void)|string
    onPaint?                     : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onPaste?                     : ((event: { source: Grid, clipboardData: string, modifiedRecords: Model[], targetCell: Location, entityName: string }) => void)|string
    onPdfExport?                 : ((event: { response?: any, error?: Error }) => void)|string
    onPointerOver?               : ((event: { sourceThe: Tooltip, event: Event }) => void)|string
    onReadOnly?                  : ((event: { readOnly: boolean }) => void)|string
    onRecompose?                 : (() => void)|string
    onRenderRow?                 : ((event: { source: Grid, row: Row, record: Model, recordIndex: number }) => void)|string
    onRenderRows?                : ((event: { source: Grid }) => void)|string
    onResize?                    : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onResponsive?                : ((event: { grid: Grid, level: string, width: number, oldLevel: string, oldWidth: number }) => void)|string
    onScroll?                    : ((event: { source: Grid, scrollTop: number }) => void)|string
    onSelectionChange?           : ((event: { action: 'select'|'deselect', mode: 'row'|'cell', source: Grid, deselected: Model[], selected: Model[], selection: Model[], deselectedCells: Location[], selectedCells: Location[], cellSelection: Location[] }) => void)|string
    onSelectionModeChange?       : ((event: { selectionMode: object }) => void)|string
    onShow?                      : ((event: { source: Widget }) => void)|string
    onSplit?                     : ((event: { subViews: GridBase[], options: { direction: 'horizontal'|'vertical'|'both', atColumn: Column, atRecord: Model } }) => void)|string
    onSplitterCollapseClick?     : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => boolean|void)|string
    onSplitterDragEnd?           : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => void)|string
    onSplitterDragStart?         : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => void)|string
    onStartCellEdit?             : ((event: { source: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onSubGridCollapse?           : ((event: { source: Grid, subGrid: SubGrid }) => void)|string
    onSubGridExpand?             : ((event: { source: Grid, subGrid: SubGrid }) => void)|string
    onToggleGroup?               : ((event: { groupRecord: Model, collapse: boolean }) => void)|string
    onToggleNode?                : ((event: { record: Model, collapse: boolean }) => void)|string
    onToolClick?                 : ((event: { source: Tool, tool: Tool }) => void)|string
    onUnsplit?                   : (() => void)|string

     // Features
    cellCopyPasteFeature?     : object|boolean|string|CellCopyPaste|Partial<CellCopyPasteConfig>
    cellEditFeature?          : object|boolean|string|GridCellEdit|Partial<GridCellEditConfig>
    cellMenuFeature?          : object|boolean|string|CellMenu|Partial<CellMenuConfig>
    cellTooltipFeature?       : object|boolean|string|CellTooltip|Partial<CellTooltipConfig>
    columnAutoWidthFeature?   : object|boolean|string|ColumnAutoWidth|Partial<ColumnAutoWidthConfig>
    columnDragToolbarFeature? : object|boolean|string|ColumnDragToolbar|Partial<ColumnDragToolbarConfig>
    columnPickerFeature?      : object|boolean|string|ColumnPicker|Partial<ColumnPickerConfig>
    columnRenameFeature?      : object|boolean|string|ColumnRename|Partial<ColumnRenameConfig>
    columnReorderFeature?     : object|boolean|string|ColumnReorder|Partial<ColumnReorderConfig>
    columnResizeFeature?      : object|boolean|string|ColumnResize|Partial<ColumnResizeConfig>
    excelExporterFeature?     : object|boolean|string|GridExcelExporter|Partial<GridExcelExporterConfig>
    fileDropFeature?          : object|boolean|string|FileDrop|Partial<FileDropConfig>
    fillHandleFeature?        : object|boolean|string|FillHandle|Partial<FillHandleConfig>
    filterFeature?            : object|boolean|string|Filter|Partial<FilterConfig>
    filterBarFeature?         : object|boolean|string|FilterBar|Partial<FilterBarConfig>
    groupFeature?             : object|boolean|string|Group|Partial<GroupConfig>
    groupSummaryFeature?      : object|boolean|string|GridGroupSummary|Partial<GridGroupSummaryConfig>
    headerMenuFeature?        : object|boolean|string|HeaderMenu|Partial<HeaderMenuConfig>
    mergeCellsFeature?        : object|boolean|string|MergeCells|Partial<MergeCellsConfig>
    pdfExportFeature?         : object|boolean|string|GridPdfExport|Partial<GridPdfExportConfig>
    printFeature?             : object|boolean|string|GridPrint|Partial<GridPrintConfig>
    quickFindFeature?         : object|boolean|string|QuickFind|Partial<QuickFindConfig>
    regionResizeFeature?      : object|boolean|string|RegionResize|Partial<RegionResizeConfig>
    rowCopyPasteFeature?      : object|boolean|string|RowCopyPaste|Partial<RowCopyPasteConfig>
    rowExpanderFeature?       : object|boolean|string|RowExpander|Partial<RowExpanderConfig>
    rowReorderFeature?        : object|boolean|string|GridRowReorder|Partial<GridRowReorderConfig>
    rowResizeFeature?         : object|boolean|string|GridRowResize|Partial<GridRowResizeConfig>
    searchFeature?            : object|boolean|string|Search|Partial<SearchConfig>
    sortFeature?              : object|boolean|string|Sort|Partial<SortConfig>
    splitFeature?             : object|boolean|string|GridSplit|Partial<GridSplitConfig>
    stickyCellsFeature?       : object|boolean|string|StickyCells|Partial<StickyCellsConfig>
    stripeFeature?            : object|boolean|string|Stripe|Partial<StripeConfig>
    summaryFeature?           : object|boolean|string|GridSummary|Partial<GridSummaryConfig>
    treeFeature?              : object|boolean|string|Tree|Partial<TreeConfig>
    treeGroupFeature?         : object|boolean|string|TreeGroup|Partial<TreeGroupConfig>

}

export class BryntumGridBase extends React.Component<BryntumGridBaseProps> {

    static instanceClass = GridBase;

    static instanceName = 'GridBase';

    processWidgetContent = processWidgetContent;

    // Component is View class
    static isView = true;

    // Hidden div to host unused portals DOM
    portalsCache? : HTMLElement;

    // CSS class used for individual portal containers
    portalContainerClass = 'b-react-portal-container';

    state = {
        // Holds React portals
        portals : new Map(),

        // Needed to trigger refresh when portals change
        generation : 0
    };

    static featureNames = [
        'cellCopyPasteFeature',
        'cellEditFeature',
        'cellMenuFeature',
        'cellTooltipFeature',
        'columnAutoWidthFeature',
        'columnDragToolbarFeature',
        'columnPickerFeature',
        'columnRenameFeature',
        'columnReorderFeature',
        'columnResizeFeature',
        'excelExporterFeature',
        'fileDropFeature',
        'fillHandleFeature',
        'filterFeature',
        'filterBarFeature',
        'groupFeature',
        'groupSummaryFeature',
        'headerMenuFeature',
        'mergeCellsFeature',
        'pdfExportFeature',
        'printFeature',
        'quickFindFeature',
        'regionResizeFeature',
        'rowCopyPasteFeature',
        'rowExpanderFeature',
        'rowReorderFeature',
        'rowResizeFeature',
        'searchFeature',
        'sortFeature',
        'splitFeature',
        'stickyCellsFeature',
        'stripeFeature',
        'summaryFeature',
        'treeFeature',
        'treeGroupFeature'
    ];

    projectStores = false;

    dataStores = {};

    static configNames = [
        'adopt',
        'align',
        'allowOver',
        'anchor',
        'anchorToTarget',
        'animateRemovingRows',
        'ariaDescription',
        'ariaLabel',
        'autoClose',
        'autoHeight',
        'autoHide',
        'autoShow',
        'autoUpdateRecord',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'callOnFunctions',
        'centered',
        'closable',
        'closeAction',
        'collapsed',
        'collapsible',
        'columns',
        'config',
        'constrainTo',
        'contentElementCls',
        'contextMenuTriggerEvent',
        'defaultBindProperty',
        'defaultFocus',
        'defaultRegion',
        'defaults',
        'destroyStore',
        'disableGridRowModelWarning',
        'dismissDelay',
        'dock',
        'draggable',
        'enableSticky',
        'enableTextSelection',
        'features',
        'fillLastColumn',
        'fixedRowHeight',
        'floating',
        'focusOnToFront',
        'footer',
        'forElement',
        'forSelector',
        'fullRowRefresh',
        'getHtml',
        'getRowHeight',
        'header',
        'hideAnimation',
        'hideDelay',
        'hideHorizontalScrollbar',
        'hideOnDelegateChange',
        'hideWhenEmpty',
        'hoverDelay',
        'htmlCls',
        'ignoreParentReadOnly',
        'itemCls',
        'keyMap',
        'lazyItems',
        'listeners',
        'loadingMsg',
        'loadMask',
        'loadMaskDefaults',
        'loadMaskError',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'maximizable',
        'maximized',
        'modal',
        'monitorResize',
        'mouseOffsetX',
        'mouseOffsetY',
        'namedItems',
        'owner',
        'plugins',
        'positioned',
        'preserveFocusOnDatasetChange',
        'preserveScrollOnDatasetChange',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'resizeToFitIncludesHeader',
        'responsiveLevels',
        'ripple',
        'rootElement',
        'scrollAction',
        'scrollerClass',
        'scrollManager',
        'selectionMode',
        'showAnimation',
        'showDirty',
        'showOnClick',
        'showOnHover',
        'showTooltipWhenDisabled',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'strips',
        'subGridConfigs',
        'syncMask',
        'tab',
        'tag',
        'tbar',
        'textAlign',
        'textContent',
        'trackMouse',
        'trapFocus',
        'ui',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'animateTreeNodeToggle',
        'appendTo',
        'cls',
        'columnLines',
        'content',
        'data',
        'dataset',
        'disabled',
        'emptyText',
        'enableUndoRedoKeys',
        'extraData',
        'flex',
        'height',
        'hidden',
        'hideFooters',
        'hideHeaders',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'longPressTime',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'onBeforeCancelCellEdit',
        'onBeforeCellDelete',
        'onBeforeCellEditStart',
        'onBeforeClose',
        'onBeforeColumnDragStart',
        'onBeforeColumnDropFinalize',
        'onBeforeCopy',
        'onBeforeDestroy',
        'onBeforeFinishCellEdit',
        'onBeforeHide',
        'onBeforePaste',
        'onBeforePdfExport',
        'onBeforeRenderRow',
        'onBeforeRenderRows',
        'onBeforeSelectionChange',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onBeforeToggleGroup',
        'onBeforeToggleNode',
        'onCancelCellEdit',
        'onCatchAll',
        'onCellClick',
        'onCellContextMenu',
        'onCellDblClick',
        'onCellMenuBeforeShow',
        'onCellMenuItem',
        'onCellMenuShow',
        'onCellMenuToggleItem',
        'onCellMouseOut',
        'onCellMouseOver',
        'onCollapse',
        'onCollapseNode',
        'onColumnDrag',
        'onColumnDragStart',
        'onColumnDrop',
        'onContextMenuItem',
        'onContextMenuToggleItem',
        'onCopy',
        'onDataChange',
        'onDestroy',
        'onDragSelecting',
        'onExpand',
        'onExpandNode',
        'onFileDrop',
        'onFinishCellEdit',
        'onFocusIn',
        'onFocusOut',
        'onGridRowAbort',
        'onGridRowBeforeDragStart',
        'onGridRowBeforeDropFinalize',
        'onGridRowDrag',
        'onGridRowDragStart',
        'onGridRowDrop',
        'onHeaderMenuBeforeShow',
        'onHeaderMenuItem',
        'onHeaderMenuShow',
        'onHeaderMenuToggleItem',
        'onHide',
        'onMouseOut',
        'onMouseOver',
        'onPaint',
        'onPaste',
        'onPdfExport',
        'onPointerOver',
        'onReadOnly',
        'onRecompose',
        'onRenderRow',
        'onRenderRows',
        'onResize',
        'onResponsive',
        'onScroll',
        'onSelectionChange',
        'onSelectionModeChange',
        'onShow',
        'onSplit',
        'onSplitterCollapseClick',
        'onSplitterDragEnd',
        'onSplitterDragStart',
        'onStartCellEdit',
        'onSubGridCollapse',
        'onSubGridExpand',
        'onToggleGroup',
        'onToggleNode',
        'onToolClick',
        'onUnsplit',
        'preserveScroll',
        'readOnly',
        'record',
        'rowHeight',
        'rowLines',
        'scrollable',
        'store',
        'strictRecordMapping',
        'title',
        'tools',
        'tooltip',
        'transitionDuration',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'isSettingValues',
        'isValid',
        'selectedCell',
        'selectedCells',
        'selectedRecord',
        'selectedRecords',
        'selectedRows',
        'state',
        'type',
        'values'
    ];

    // Component instance
    instance!: GridBase;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumGridBaseProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-grid-base-container`;
        return (
            <React.Fragment>
                <div className={className} ref={(element) => (this.element = element!)}>
                    {Array.from(this.state.portals).map((item) => item[1])}
                </div>
                <div ref={el => (this.portalsCache = el!)} className="b-react-portals-cache" style={{ display : 'none' }}></div>
            </React.Fragment>
        );

    }
}
